import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Badge, Switch, Tooltip, Space, Button } from 'antd';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  UserOutlined,
  HeartOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { useLiveStatistics } from '../../hooks/useRealtimeAnalytics';

const RealtimeDashboard = ({ style }) => {
  const { stats, isLive, startLiveUpdates, stopLiveUpdates } = useLiveStatistics();
  const [historicalData, setHistoricalData] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Generate historical data for charts
  useEffect(() => {
    const generateHistoricalData = () => {
      const now = new Date();
      const data = [];
      
      for (let i = 29; i >= 0; i--) {
        const time = new Date(now.getTime() - i * 2 * 60 * 1000); // 2-minute intervals
        data.push({
          time: time.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          screenings: Math.floor(Math.random() * 8) + 2,
          activeUsers: Math.floor(Math.random() * 15) + 5,
          responseTime: Math.floor(Math.random() * 20) + 20
        });
      }
      
      setHistoricalData(data);
    };

    generateHistoricalData();
    
    if (autoRefresh) {
      const interval = setInterval(generateHistoricalData, 30000); // Update every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const handleLiveToggle = (checked) => {
    if (checked) {
      const cleanup = startLiveUpdates();
      setAutoRefresh(true);
      return cleanup;
    } else {
      stopLiveUpdates();
      setAutoRefresh(false);
    }
  };

  const refreshData = () => {
    // Force refresh of historical data
    const now = new Date();
    const newData = historicalData.slice(1);
    newData.push({
      time: now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      screenings: Math.floor(Math.random() * 8) + 2,
      activeUsers: Math.floor(Math.random() * 15) + 5,
      responseTime: Math.floor(Math.random() * 20) + 20
    });
    setHistoricalData(newData);
  };

  return (
    <div style={style}>
      {/* Real-time Controls */}
      <Card 
        title="Real-time Monitoring" 
        size="small"
        extra={
          <Space>
            <Tooltip title={isLive ? "Pause live updates" : "Start live updates"}>
              <Switch
                checked={isLive}
                onChange={handleLiveToggle}
                checkedChildren={<PlayCircleOutlined />}
                unCheckedChildren={<PauseCircleOutlined />}
              />
            </Tooltip>
            <Tooltip title="Refresh data">
              <Button
                icon={<ReloadOutlined />}
                onClick={refreshData}
                size="small"
              />
            </Tooltip>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Active Users"
              value={stats.activeUsers}
              prefix={<UserOutlined />}
              suffix={
                <Badge 
                  status={isLive ? "processing" : "default"} 
                  text={isLive ? "LIVE" : "PAUSED"}
                />
              }
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Ongoing Screenings"
              value={stats.ongoingScreenings}
              prefix={<HeartOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Today's Screenings"
              value={stats.todayScreenings}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Avg Response Time"
              value={stats.avgResponseTime}
              suffix="ms"
              prefix={<ThunderboltOutlined />}
              valueStyle={{ 
                color: stats.avgResponseTime < 30 ? '#3f8600' : 
                       stats.avgResponseTime < 50 ? '#cf1322' : '#722ed1'
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Real-time Charts */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Live Activity" size="small">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={historicalData.slice(-15)}>
                <defs>
                  <linearGradient id="colorScreenings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#52c41a" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <RechartsTooltip />
                <Area 
                  type="monotone" 
                  dataKey="screenings" 
                  stroke="#1890ff" 
                  fillOpacity={1} 
                  fill="url(#colorScreenings)" 
                  name="Screenings"
                />
                <Area 
                  type="monotone" 
                  dataKey="activeUsers" 
                  stroke="#52c41a" 
                  fillOpacity={1} 
                  fill="url(#colorUsers)" 
                  name="Active Users"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Response Time Trend" size="small">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={historicalData.slice(-15)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <RechartsTooltip />
                <Line 
                  type="monotone" 
                  dataKey="responseTime" 
                  stroke="#ff7875" 
                  strokeWidth={2}
                  dot={{ fill: '#ff7875', r: 3 }}
                  name="Response Time (ms)"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Live Status Indicators */}
      <Card title="System Status" size="small" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Badge status="processing" />
              <span style={{ marginLeft: 8 }}>Database: Online</span>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Badge status="success" />
              <span style={{ marginLeft: 8 }}>API: Healthy</span>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Badge status={stats.avgResponseTime < 50 ? "success" : "warning"} />
              <span style={{ marginLeft: 8 }}>Performance: {stats.avgResponseTime < 50 ? "Good" : "Slow"}</span>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Badge status="success" />
              <span style={{ marginLeft: 8 }}>Queue: Active</span>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default RealtimeDashboard;
